import {
  OPERATION_DEFINITIONS,
  OPERATION_IDS,
  projectFromDefinitions,
  type ReferenceGroupKey,
} from './operation-definitions.js';
import type { OperationId } from './types.js';

export type { ReferenceGroupKey } from './operation-definitions.js';

export interface ReferenceOperationGroupDefinition {
  key: ReferenceGroupKey;
  title: string;
  description: string;
  pagePath: string;
  operations: readonly OperationId[];
}

export const OPERATION_REFERENCE_DOC_PATH_MAP: Record<OperationId, string> = projectFromDefinitions(
  (_id, entry) => entry.referenceDocPath,
);

const GROUP_METADATA: Record<ReferenceGroupKey, { title: string; description: string; pagePath: string }> = {
  core: {
    title: 'Core',
    description: 'Primary read and write operations.',
    pagePath: 'core/index.mdx',
  },
  blocks: {
    title: 'Blocks',
    description: 'Block-level structural operations.',
    pagePath: 'blocks/index.mdx',
  },
  capabilities: {
    title: 'Capabilities',
    description: 'Runtime support discovery for capability-aware branching.',
    pagePath: 'capabilities/index.mdx',
  },
  create: {
    title: 'Create',
    description: 'Structured creation helpers.',
    pagePath: 'create/index.mdx',
  },
  sections: {
    title: 'Sections',
    description: 'Section structure and page-setup operations.',
    pagePath: 'sections/index.mdx',
  },
  format: {
    title: 'Format',
    description: "Canonical formatting mutation with directive semantics ('on', 'off', 'clear').",
    pagePath: 'format/index.mdx',
  },
  styles: {
    title: 'Styles',
    description: 'Document-level stylesheet mutations (docDefaults, style definitions).',
    pagePath: 'styles/index.mdx',
  },
  lists: {
    title: 'Lists',
    description: 'List inspection and list mutations.',
    pagePath: 'lists/index.mdx',
  },
  comments: {
    title: 'Comments',
    description: 'Comment authoring and thread lifecycle operations.',
    pagePath: 'comments/index.mdx',
  },
  trackChanges: {
    title: 'Track Changes',
    description: 'Tracked-change inspection and review operations.',
    pagePath: 'track-changes/index.mdx',
  },
  query: {
    title: 'Query',
    description: 'Deterministic selector-based queries for mutation targeting.',
    pagePath: 'query/index.mdx',
  },
  mutations: {
    title: 'Mutations',
    description: 'Atomic mutation plan preview and execution.',
    pagePath: 'mutations/index.mdx',
  },
  'format.paragraph': {
    title: 'Paragraph Formatting',
    description: 'Paragraph-level direct formatting: alignment, indentation, spacing, borders, shading, and more.',
    pagePath: 'format/paragraph/index.mdx',
  },
  'styles.paragraph': {
    title: 'Paragraph Styles',
    description: 'Paragraph style reference operations (set/clear w:pStyle).',
    pagePath: 'styles/paragraph/index.mdx',
  },
  tables: {
    title: 'Tables',
    description: 'Table structure, layout, styling, and cell operations.',
    pagePath: 'tables/index.mdx',
  },
  history: {
    title: 'History',
    description: 'Undo/redo history state and navigation.',
    pagePath: 'history/index.mdx',
  },
  toc: {
    title: 'Table of Contents',
    description: 'Table of contents lifecycle and configuration.',
    pagePath: 'toc/index.mdx',
  },
  images: {
    title: 'Images',
    description: 'Image lifecycle, placement, and wrap configuration.',
    pagePath: 'images/index.mdx',
  },
  hyperlinks: {
    title: 'Hyperlinks',
    description: 'Hyperlink discovery, creation, and metadata management.',
    pagePath: 'hyperlinks/index.mdx',
  },
};

export const REFERENCE_OPERATION_GROUPS: readonly ReferenceOperationGroupDefinition[] = (
  Object.keys(GROUP_METADATA) as ReferenceGroupKey[]
).map((key) => ({
  key,
  ...GROUP_METADATA[key],
  operations: OPERATION_IDS.filter((id) => OPERATION_DEFINITIONS[id].referenceGroup === key),
}));
